import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Clock, XCircle, WhatsappLogo, CreditCard } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import { PlanCard, usePlans } from "@/components/Pricing";
import { WHATSAPP_URL } from "@/components/TrialBanner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const STATUS_TEXT = { trial: "Ücretsiz deneme", active: "Aktif", expired: "Süresi doldu" };

export default function Plan() {
  const { user } = useAuth();
  const data = usePlans();
  const [busy, setBusy] = useState("");
  const [history, setHistory] = useState([]);
  const t = user?.tenant;
  const isOwner = user?.role === "owner";

  useEffect(() => { if (isOwner && !t?.platform_admin) api.get("/billing/history").then((r) => setHistory(r.data)).catch(() => {}); }, [isOwner, t?.platform_admin]);

  const buy = async (plan) => {
    setBusy(plan);
    try {
      const { data: d } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      window.location.assign(d.url);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy("");
    }
  };

  if (!t) return null;
  return (
    <div data-testid="plan-page">
      <PageHeader eyebrow="Hesap / Abonelik" title="Planım" />
      <div className="grid gap-4 lg:grid-cols-[1fr_auto]">
        <div className="border bg-white p-5" data-testid="plan-summary">
          <div className="label-eyebrow">Mevcut durum</div>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <span className="font-display text-2xl font-bold" data-testid="plan-summary-name">{t.platform_admin ? "Pro · Yönetici" : t.plan_name}</span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${t.status === "expired" ? "bg-red-100 text-red-700" : t.status === "trial" ? "bg-orange-100 text-orange-800" : "bg-teal-100 text-teal-800"}`} data-testid="plan-summary-status">
              {t.platform_admin ? "Sınırsız" : STATUS_TEXT[t.status]}
            </span>
          </div>
          {!t.platform_admin && t.days_left != null && (
            <p className="mt-2 text-sm text-slate-600" data-testid="plan-summary-days">
              {t.status === "expired" ? "Süreniz doldu; yeni veri girişi kilitli. Bir plan seçerek hemen devam edebilirsiniz." : `${t.days_left} gün kaldı · bitiş ${new Date(t.status === "active" ? t.paid_until : t.trial_ends_at).toLocaleDateString("tr-TR")}`}
            </p>
          )}
          {t.platform_admin && <p className="mt-2 text-sm text-slate-600">Yönetici hesabında tüm özellikler her zaman açıktır.</p>}
        </div>
        <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="flex items-center gap-3 border bg-[#25D366]/10 p-5 transition-colors hover:bg-[#25D366]/20" data-testid="plan-whatsapp">
          <WhatsappLogo weight="fill" size={28} className="text-[#128C7E]" />
          <div className="text-sm"><div className="font-bold">Havale / EFT ile ödemek ister misiniz?</div><div className="text-slate-600">WhatsApp: 0533 070 74 31 — planınızı biz tanımlayalım.</div></div>
        </a>
      </div>
      {!t.platform_admin && data?.plans && (
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {data.plans.map((p, i) => (
            <PlanCard key={p.id} plan={p} dark={false} delay={i * 100} current={t.status === "active" && t.plan === p.id} action={
              isOwner ? (
                <Button onClick={() => buy(p.id)} disabled={!!busy} className="w-full rounded-[4px] bg-zinc-950 font-bold hover:bg-zinc-800" data-testid={`plan-buy-${p.id}`}>
                  <CreditCard className="mr-2" /> {busy === p.id ? "Yönlendiriliyor…" : `Kartla öde · 30 gün`}
                </Button>
              ) : <p className="text-xs text-slate-500">Satın alma işlemini işveren hesabı yapabilir.</p>
            } />
          ))}
        </div>
      )}
      {history.length > 0 && (
        <div className="mt-8 border bg-white p-5" data-testid="plan-history">
          <h3 className="font-display text-lg font-bold">Ödeme geçmişi</h3>
          <table className="mt-3 w-full text-sm">
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-t">
                  <td className="py-2">{new Date(h.created_at).toLocaleString("tr-TR")}</td>
                  <td className="py-2 font-semibold capitalize">{h.plan}</td>
                  <td className="py-2 font-num">{h.amount ? `${h.amount.toLocaleString("tr-TR")} TL` : "Manuel tanımlama"}</td>
                  <td className="py-2">{{ paid: "Ödendi", pending: "Bekliyor", expired: "Süresi doldu", manual: "Yönetici tanımladı" }[h.payment_status] || h.payment_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function PlanResult() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState("pending");
  const [plan, setPlan] = useState("");

  useEffect(() => {
    if (!sid) return setState("failed");
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        setPlan(data.plan);
        if (data.payment_status === "paid") return setState("paid");
        if (data.payment_status === "expired") return setState("failed");
      } catch { return setState("failed"); }
      tries += 1;
      if (tries < 15) timer = setTimeout(poll, 2000); else setState("failed");
      return undefined;
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid]);

  const V = { paid: [CheckCircle, "text-teal-600", "Planınız etkinleştirildi", `${plan} planı 30 gün boyunca açık. İyi çalışmalar!`],
    pending: [Clock, "text-orange-400", "Ödeme kontrol ediliyor…", "Lütfen bu sayfayı kapatmayın."],
    failed: [XCircle, "text-red-600", "Ödeme tamamlanamadı", "Ödeme onaylanmadı. Planım sayfasından tekrar deneyebilirsiniz."] };
  const [Icon, tone, title, text] = V[state];
  return (
    <div className="mx-auto max-w-md py-16 text-center" data-testid="plan-result">
      <Icon size={56} weight="fill" className={`mx-auto ${tone}`} />
      <h1 className="mt-4 font-display text-2xl font-bold" data-testid="plan-result-title">{title}</h1>
      <p className="mt-2 text-sm text-slate-600 capitalize">{text}</p>
      <Button asChild className="mt-6 rounded-[4px]" onClick={() => window.location.assign("/plan")}><Link to="/plan" onClick={() => setTimeout(() => window.location.reload(), 50)} data-testid="plan-result-back">Planım sayfasına dön</Link></Button>
    </div>
  );
}
